import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Search, Visibility, ContentCopy } from '@mui/icons-material';
import { ModelConfig } from '../RbacConfig';

type Props = {
  model: ModelConfig;
  rows: any[];
  onAdd: () => void;
  onEdit: (row: any) => void;
  onDelete: (row: any) => void;
  onClone?: (row: any) => void;
  loading?: boolean;
  actionButton?: React.ReactNode;
  rowKey?: string;
  emptyLabel?: string;

  canAdd?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canClone?: boolean;
  readOnly?: boolean;
};

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
};

const getRowId = (row: any, rowKey?: string) => {
  if (rowKey && row?.[rowKey] != null) return row[rowKey];
  return row?.id ?? row?.Id;
};

const normalizeSortValue = (v: any) => {
  if (v == null) return '';
  if (typeof v === 'string') return v.toLowerCase();
  return v;
};

const truncateWithSpaceEllipsis = (value: string, at?: number) => {
  const s = value ?? '';
  if (!at || at <= 0) return s;
  if (s.length <= at) return s;
  return s.slice(0, at).trimEnd() + ' ...';
};

const pickDefaultSortKey = (columns: any[]): string => {
  const keys = new Set((columns || []).map((c) => c.key));
  if (keys.has('name')) return 'name';
  if (keys.has('id')) return 'id';
  if (keys.has('Id')) return 'Id';
  return columns?.[0]?.key ?? 'id';
};

export function AdminTable({
                             model,
                             rows,
                             onAdd,
                             onEdit,
                             onDelete,
                             onClone,
                             loading = false,
                             actionButton,
                             rowKey,
                             emptyLabel = 'No records found.',
                             canAdd = true,
                             canEdit = true,
                             canDelete = true,
                             canClone = true,
                             readOnly = false,
                           }: Props) {
  const [searchTerm, setSearchTerm] = useState('');

  const [sortConfig, setSortConfig] = useState<SortConfig>(() => ({
    key: pickDefaultSortKey(model.columns as any),
    direction: 'asc',
  }));

  useEffect(() => {
    const keys = new Set((model.columns as any[])?.map((c) => c.key) || []);
    setSortConfig((prev) => {
      if (!prev?.key || !keys.has(prev.key)) {
        return { key: pickDefaultSortKey(model.columns as any), direction: 'asc' };
      }
      return prev;
    });
  }, [model.columns]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows || [];
    const cols = model.columns as any[];

    return (rows || []).filter((row) =>
      cols.some((col) => {
        const value = row?.[col.key];
        if (value == null) return false;
        return String(value).toLowerCase().includes(searchTerm);
      }),
    );
  }, [rows, searchTerm, model.columns]);

  const sortedRows = useMemo(() => {
    const { key, direction } = sortConfig;

    return [...filteredRows].sort((a, b) => {
      const av = normalizeSortValue(a?.[key]);
      const bv = normalizeSortValue(b?.[key]);

      if (av < bv) return direction === 'asc' ? -1 : 1;
      if (av > bv) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortConfig]);

  const getCellSx = (col: any) => {
    const manualTruncate = !!col.truncateAt && col.truncateAt > 0;

    return {
      minWidth: col.minWidth,
      maxWidth: col.maxWidth,
      ...(manualTruncate
        ? { whiteSpace: 'normal', wordBreak: 'break-word' }
        : col.truncate
          ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
          : { whiteSpace: 'normal', wordBreak: 'break-word' }),
    };
  };

  const showActions = canEdit || canDelete || canClone || readOnly;

  return (
    <Paper sx={{ mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
        <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 240 }}>
          <Typography variant="h6">{model.label}</Typography>
          {actionButton && <Box>{actionButton}</Box>}
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            onChange={handleSearchChange}
            value={searchTerm}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button variant="contained" startIcon={<Add />} onClick={onAdd} disabled={loading || !canAdd}>
            Add
          </Button>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {(model.columns as any[]).map((col: any) => (
                <TableCell
                  key={col.key}
                  sx={{
                    minWidth: col.minWidth,
                    maxWidth: col.maxWidth,
                    whiteSpace: col.headerNoWrap === false ? 'normal' : 'nowrap',
                  }}
                >
                  <TableSortLabel
                    active={sortConfig?.key === col.key}
                    direction={sortConfig?.key === col.key ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ width: 180, whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={(model.columns as any[]).length + 1}>
                  <Box display="flex" alignItems="center" gap={2} py={3}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading…</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={(model.columns as any[]).length + 1}>
                  <Box py={3}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyLabel}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row) => {
                const rid = getRowId(row, rowKey);

                return (
                  <TableRow key={String(rid)} hover>
                    {(model.columns as any[]).map((col: any) => (
                      <TableCell key={col.key} sx={{ verticalAlign: 'top', ...getCellSx(col) }}>
                        {(() => {
                          if (typeof row?.[col.key] === 'boolean') {
                            return <Checkbox checked={!!row[col.key]} disabled size="small" />;
                          }

                          if (col.render) {
                            return col.render(row);
                          }

                          const raw = row?.[col.key];
                          const text = raw == null ? '' : String(raw);

                          if (col.truncateAt && col.truncateAt > 0) {
                            const truncated = truncateWithSpaceEllipsis(text, col.truncateAt);
                            const isTruncated = truncated !== text;

                            return (
                              <Tooltip
                                title={isTruncated ? text : ''}
                                placement="top"
                                arrow
                                disableHoverListener={!isTruncated}
                              >
                                <Box component="span" sx={{ display: 'inline-block', maxWidth: '100%' }}>
                                  {truncated}
                                </Box>
                              </Tooltip>
                            );
                          }

                          if (col.truncate) {
                            return (
                              <Tooltip
                                title={text}
                                placement="top"
                                arrow
                                disableHoverListener={col.tooltipOnTruncate === false}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {text}
                                </Box>
                              </Tooltip>
                            );
                          }

                          return text;
                        })()}
                      </TableCell>
                    ))}

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {!showActions ? null : (
                        <>
                          {readOnly ? (
                            <Tooltip title="View">
                              <IconButton onClick={() => onEdit(row)} size="small">
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : canEdit ? (
                            <Tooltip title="Edit">
                              <IconButton onClick={() => onEdit(row)} size="small">
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="View">
                              <IconButton onClick={() => onEdit(row)} size="small">
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {!readOnly && canClone && onClone && (
                            <Tooltip title="Clone">
                              <IconButton onClick={() => onClone(row)} size="small">
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton onClick={() => onDelete(row)} size="small">
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}